<template>
  <UiDropdown class="w-full">
    <template #default="{ open }">
      <div class="gap-2 w-full group cursor-pointer" :class="{ flex: !compact }">
        <div class="relative w-12 h-12" :class="{ 'mx-auto': compact }">
          <div
            v-if="!userRank.data.rank"
            :title="uInfo.name"
            class="mx-auto ring-0 hover:ring-2 group-hover:scale-105 ring-neutral-500/40 transition-all h-10 w-10 shrink-0 rounded-full overflow-hidden bg-neutral-200 dark:bg-neutral-600 cursor-pointer"
          >
            <img
              v-if="uInfo.avatar"
              :src="uInfo.avatar"
              class="h-full w-full object-cover rounded-full overflow-hidden"
              alt="avatar"
            />
            <div v-else class="text-center w-full h-full flex items-center justify-center text-lg text-[--c-primary-900] bg-[--c-primary-400] dark:text-[--c-primary-at-600]">
              {{ getInitials(uInfo.name) || '' }}
            </div>
          </div>
          <div v-else class="group-hover:scale-105 transition-all -mt-1">
            <UiGameAvatar
              :rank="userRank.data.rank"
              :alt="uInfo?.name"
              :role="uInfo?.role"
              :img="uInfo?.avatar"
              compact
            />
          </div>
          <template v-if="userBirthday || notifications.unreadCount > 0">
            <div class="animate-ping absolute top-0 right-0 bg-red-400 w-2 h-2 rounded-full"></div>
            <div class="absolute top-0 right-0 bg-red-400 w-2 h-2 rounded-full"></div>
          </template>
          <ModalProfile ref="refProfile" />
        </div>

        <div v-if="!compact" class="max-w-[calc(100%-54px)] flex flex-col h-12" :title="uInfo.name || ''">
          <div class="flex gap-2 font-semibold mb-1">
            <div class="text-sm leading-4 line-clamp-2">{{ uInfo.name || '' }}</div>
            <UiIcon icon="chevron-up" :size="16" stroke-width="2" :class="['shrink-0 transition-transform', { 'rotate-180': open }]" />
          </div>
          <div class="text-xs leading-[12px] text-neutral-500 dark:text-neutral-300">
            {{ uInfo.role }}
          </div>
        </div>
      </div>
    </template>

    <!-- User Menu -->
    <template #content>
      <div class="w-64">
        <UiMenuItem
          :disabled="!!authStore.clientMode.tenant"
          class="py-3"
          @click="() => refProfile?.open()"
        >
          <div v-if="authStore.clientMode.tenant" class="tooltip tooltip-left" data-tip="Desabilitado no modo cliente">
            {{ $t('topbar.profile') }}
          </div>
          <template v-else>
            <div class="flex items-center justify-between">
              {{ $t('topbar.profile') }}
              <span v-if="userBirthday" class="relative">
                <UiIcon icon="confetti" :size="24" />
                <div class="animate-ping absolute top-0 right-0 bg-red-400 w-2 h-2 rounded-full"></div>
                <div class="absolute top-0 right-0 bg-red-400 w-2 h-2 rounded-full"></div>
              </span>
            </div>
          </template>
        </UiMenuItem>

        <!-- Notificações -->
        <UiMenuItem @click="() => notifyBar?.open()">
          <div class="flex items-center justify-between">
            {{ $t('menu.notifications') }}
            <div class="relative">
              <UiIcon icon="bell" :size="20" />
              <div
                v-if="notifications.unreadCount > 0"
                :title="`${notifications.unreadCount} ${$t('tooltip.unreadNotifications')}`"
                class="absolute -top-2 -right-2 text-xs font-semibold px-1 min-w-[12px] min-h-[12px] text-white rounded-full bg-red-500"
              >
                {{ notifications.unreadCount }}
              </div>
            </div>
          </div>
        </UiMenuItem>

        <!-- Gamificação -->
        <UiMenuItem @click="toggleGame">
          <div class="flex items-center justify-between">
            {{ $t('menu.gamification') }}
            <img :src="ImgGamePoints" alt="points" width="20" height="20" />
          </div>
        </UiMenuItem>

        <!-- Config -->
        <UiMenuItem v-if="authStore.subscription.id" to="/user-settings">{{ $t('topbar.settings') }}</UiMenuItem>
        <UiSeparator />

        <!-- Temas -->
        <UiMenuItem 
          :disabled="blockTheme" 
          :close-on-select="false" 
          v-tooltip
          :title="blockTheme ? $t('mainContent.customizations.theme.blockTheme', { theme: $t(systemStore.isDark ? 'labels.dark' : 'labels.light')?.toLowerCase() }) : ''"
          @click="() => systemStore.toggleTheme()"
        >
          <div class="flex items-center justify-between">
            {{ systemStore.isDark ? $t('button.lightMode') : $t('button.darkMode') }}
            <transition
              mode="out-in"
              enter-active-class="transition ease-out duration-150 transform"
              enter-from-class="opacity-0 translate-y-4"
              leave-active-class="ease-in duration-200"
              leave-to-class="opacity-0 translate-y-4"
            >
              <UiIcon v-if="systemStore.isDark" :size="20" icon="sun-high" />
              <UiIcon v-else :size="20" icon="moon" />
            </transition>
          </div>
        </UiMenuItem>

        <!-- Idiomas -->
        <UiMenuItem>
          <UiLangSelector :text="$t('language.change')" size="sm" />
        </UiMenuItem>

        <!-- Termos de uso -->
        <UiMenuItem
          v-if="systemStore.clientSettings?.settings?.general?.termOfUse"
          @click="() => systemStore.openTermsOfUse(null, 'preview')"
        >
          {{ $t('topbar.termsAndConditions') }}
        </UiMenuItem>

        <UiMenuItem @click="() => communicationsRef.open()">{{ $t('menu.communications') }}</UiMenuItem>
        <UiMenuItem
          v-if="uInfo.multiplePartners"
          to="/partner"
          :disabled="!!authStore.clientMode.tenant"
          @click="() => webStorage.removeItem('show-page-partner')"
        >
          <div v-if="authStore.clientMode.tenant" class="tooltip tooltip-left" data-tip="Desabilitado no modo cliente">
            {{ $t('topbar.changeCompany') }}
          </div>
          <template v-else>{{ $t('topbar.changeCompany') }}</template>
        </UiMenuItem>
        <UiMenuItem v-if="!systemStore.getConfig.hideSupport" @click="() => toggleHelp()">
          <div class="flex items-center justify-between">
            {{ $t('placeholder.help') }}
            <UiIcon icon="help-circle" size="20" />
          </div>
        </UiMenuItem>
        <UiSeparator />
        <UiMenuItem to="/logout">{{ $t('topbar.logout') }}</UiMenuItem>
      </div>
    </template>
  </UiDropdown>

  <ViewCommunication showAllCommunication ref="communicationsRef" />
  <NotificationsBar ref="notifyBar" />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAuthStore, useGamificacaoStore, useSystemStore, storeToRefs, useUserNotificationsStore } from '@/stores'
import { useSessionStorage } from '@vueuse/core'
import { getInitials } from '@/common/util'
import webStorage from '@/common/webStorage'

import UiDropdown from '@/components/ui/dropdown.vue'
import UiIcon from '@/components/ui/icon.vue'
import UiGameAvatar from '@/components/ui/gamification/avatar.vue'
import UiLangSelector from '@/components/ui/langSelector.vue'
import UiMenuItem from '@/components/ui/menuItem.vue'
import UiSeparator from '@/components/ui/separator.vue'

import ModalProfile from '@/components/modais/profile/index.vue'
import ViewCommunication from '@/components/modais/comunicacao/modalViewCommunication.vue'
import NotificationsBar from './notificationsBar.vue'

import ImgGamePoints from '@/assets/game/icon-points.svg'

const { compact = false } = defineProps<{ compact?: boolean }>()

const authStore = useAuthStore()
const systemStore = useSystemStore()
const gamificationStore = useGamificacaoStore()
const userNotifications = useUserNotificationsStore()

const settings = ref(systemStore.clientSettings)

const refProfile = ref<InstanceType<typeof ModalProfile> | null>()
const communicationsRef = ref()
const notifyBar = ref<InstanceType<typeof NotificationsBar>>()

const uInfo = computed(() => authStore.persistentInfo)
const { userRank } = storeToRefs(gamificationStore)

const notifications = computed(() => ({ unreadCount: userNotifications.unreadCount || 0 }))

const userBirthday = computed(() => {
  if (!uInfo.value.birthdate) return false

  const bd = new Date(uInfo.value.birthdate)
  const today = new Date()

  return bd.getDate() === today.getDate() && bd.getMonth() === today.getMonth()
})

const blockTheme = computed(() => {
  return settings.value?.settings?.theme?.defaultTheme !== 'DefinidoPeloUsuario'
})

const toggleGame = () => {
  gamificationStore.getMyRank()
  gamificationStore.getRank({ page: 1, size: 5 })
  gamificationStore.getTrophies({ page: 1, size: 6 })

  const gameBar = useSessionStorage<any>('game-bar', false)
  gameBar.value = !gameBar.value
}

const toggleHelp = () => {
  (document.getElementsByClassName('md-chat-widget-btn-wrapper')[0] as HTMLElement)?.click()
}
</script>
